import React, { ReactElement, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import {
    createFailure,
    createListTableColumns,
    createValidationResult,
    depascalize,
    ensure,
    FailureSeverity,
    FormDialog,
    FormFields,
    FormTextField,
    FormValidationSummary,
    Icon,
    renderDateTime,
    renderTextWithTooltip,
    ShimmeredDetailsList,
    Spinner,
    SpinnerSize,
    Stack,
    useNotifications,
    ValidationFailure
} from "sprint-common-web";

import { OrderStatus } from "../../../common/types/OrderStatus";
import { useOrdersModuleApi } from "../../../common/useOrdersModuleApi";
import { OrderListingContext } from "../../listing/context/OrderListingContext";
import { OrderListingItem } from "../../listing/OrderListingItem";

interface RejectOrderItem {
    id: string;
    content: string;
    pONumber: string;
    targetPath: string;
    systemName: string;
    startDate: string;
    orderStatus: OrderStatus;
    buzzId?: string;
}

type ProcessRejectOrderItem = RejectOrderItem & {
    processStatus: ActionStatus;
};

interface RejectMultipleOrdersDialogProps {
    selectedItems: RejectOrderItem[];
    onRejectingComplete(successfullyRejectedOrders: RejectOrderItem[]): void;
}

enum ActionStatus {
    WaitingForConfirmation,
    InProgress,
    Completed,
    Failed
}

export const RejectMultipleOrdersDialog: React.VFC<RejectMultipleOrdersDialogProps> = ({
    selectedItems,
    onRejectingComplete: onRemovingComplete
}) => {
    const { rejectOrder } = useOrdersModuleApi();

    const { reject: rejectOrderInContext } = useContext(OrderListingContext);
    const [rejectReason, setRejectReason] = useState<string | undefined>(undefined);
    const { success, error: errorNotification } = useNotifications();

    const { formState, handleSubmit, errors, control } = useForm();

    const [isActionInProgress, setIsActionInProgress] = useState<boolean>(false);
    const [items, setItems] = useState<ProcessRejectOrderItem[]>(
        selectedItems
            .filter((selectedItem) => isOrderItemRejectable(selectedItem))
            .map((item) => ({ ...item, processStatus: ActionStatus.WaitingForConfirmation }))
    );

    const setStatusById = (id: string, status: ActionStatus) => {
        setItems((prevItems) =>
            prevItems.map((prevItem) => (prevItem.id === id ? { ...prevItem, processStatus: status } : prevItem))
        );
    };

    const formHasErrors = createValidationResult([validateDiscardedItems(items, selectedItems.length)]);
    const areValidOrdersEmpty = !items.length;

    const handleRemovingComplete = () => {
        const removingCompletedItems = items.filter(({ processStatus }) => processStatus === ActionStatus.Completed);

        onRemovingComplete(removingCompletedItems.map(({ processStatus, ...item }) => item));
    };

    const handleRejectReasonChange = (reason: string) => setRejectReason(reason);

    const reject = async () => {
        const results = await rejectAllItems();

        if (results.every(({ status }) => status === "fulfilled")) {
            onRemovingComplete(items.map(({ processStatus, ...item }) => item));

            success("Rejecting complete");
        } else errorNotification("Something went wrong while rejecting orders");
    };

    const rejectAllItems = async () => {
        setIsActionInProgress(true);

        const results = await Promise.allSettled(
            items.map(async (item) => {
                setStatusById(item.id, ActionStatus.InProgress);

                try {
                    ensure(rejectReason);
                    await rejectOrder({ orderId: item.id, rejectionReason: rejectReason });

                    setStatusById(item.id, ActionStatus.Completed);

                    rejectOrderInContext(item.id, rejectReason);
                } catch (error) {
                    setStatusById(item.id, ActionStatus.Failed);

                    throw error;
                }
            })
        );

        setIsActionInProgress(false);

        return results;
    };

    return (
        <Stack>
            <FormDialog
                title={"Reject orders"}
                formState={formState}
                text={"Are you sure you want to start rejecting listed orders?"}
                isBlocking={isActionInProgress}
                showCloseButton={false}
                submit={{
                    onClick: handleSubmit(reject),
                    disabled: areValidOrdersEmpty || isActionInProgress
                }}
                cancel={{
                    onClick: handleRemovingComplete
                }}
                width={1400}
                contentHeight={400}
            >
                <FormFields>
                    <FormValidationSummary validationResult={formHasErrors} />
                </FormFields>
                {!!items.length && (
                    <Stack>
                        <ItemsSummary items={items} />
                        <FormTextField
                            label="Reject reason"
                            control={control}
                            name="rejectReason"
                            errors={errors}
                            multiline={true}
                            onInputChange={handleRejectReasonChange}
                            rules={{ required: "Enter reject reason" }}
                        ></FormTextField>
                    </Stack>
                )}
            </FormDialog>
        </Stack>
    );
};

const orderDeliveryFailedStr = depascalize(OrderStatus[OrderStatus.DeliveryFailed]);
const orderProcessingCompletedStr = depascalize(OrderStatus[OrderStatus.Encoded]);
const orderProcessingFailedStr = depascalize(OrderStatus[OrderStatus.EncodingFailed]);
const discardedOrdersInfoMessage = `Orders with status other than '${orderDeliveryFailedStr}', '${orderProcessingCompletedStr}' and '${orderProcessingFailedStr}', and those coming from Buzz or Shreq have been discarded`;

const validateDiscardedItems = (items: RejectOrderItem[], selectedItemsCount: number): ValidationFailure | void => {
    if (!items.length) {
        return createFailure(
            `${discardedOrdersInfoMessage}. Nothing to reject.`,
            "no-orders-to-deliver",
            FailureSeverity.Error
        );
    } else if (items.length < selectedItemsCount) {
        return createFailure(discardedOrdersInfoMessage, "orders-discarded-from-delivery", FailureSeverity.Warning);
    }
};

interface ItemsSummaryProps {
    items: ProcessRejectOrderItem[];
}

const ItemsSummary: React.VFC<ItemsSummaryProps> = ({ items }: ItemsSummaryProps) => {
    const columns = createListTableColumns<ProcessRejectOrderItem>([
        {
            type: "basic",
            key: "pONumber",
            name: "PO number",
            minWidth: 200
        },
        {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 200
        },
        {
            type: "basic",
            key: "targetPath",
            name: "Target path",
            minWidth: 250,
            onRender: renderTextWithTooltip
        },
        {
            type: "basic",
            key: "systemName",
            name: "System",
            minWidth: 150
        },
        {
            type: "basic",
            key: "startDate",
            name: "Start date",
            minWidth: 150,
            onRender: renderDateTime
        },
        {
            type: "basic",
            key: "processStatus",
            name: "Process status",
            minWidth: 100,
            onRender: renderActionStatus
        }
    ]);

    return <ShimmeredDetailsList columns={columns} items={items} layoutMode="fixedColumns" />;
};

export const renderActionStatus = (item?: ProcessRejectOrderItem): ReactElement | null => {
    ensure(item);
    const status = item["processStatus"];

    const indicator = (() => {
        switch (status) {
            case ActionStatus.WaitingForConfirmation: {
                return <></>;
            }

            case ActionStatus.InProgress: {
                return <Spinner size={SpinnerSize.small} />;
            }

            case ActionStatus.Completed: {
                return <Icon iconName="Accept" />;
            }

            case ActionStatus.Failed: {
                return <Icon iconName="Cancel" />;
            }
        }
    })();

    return <Stack horizontalAlign="start">{indicator}</Stack>;
};

const isOrderItemRejectable = (item: RejectOrderItem) =>
    (item.orderStatus === OrderStatus.Encoded ||
        item.orderStatus === OrderStatus.EncodingFailed ||
        item.orderStatus === OrderStatus.DeliveryFailed) &&
    !item.buzzId;

export const mapToRejectOrderItem = (order: OrderListingItem): RejectOrderItem => {
    return {
        id: order.id,
        content: order.content,
        pONumber: order.pONumber,
        orderStatus: order.status,
        targetPath: order.targetFilename,
        systemName: order.system.name,
        startDate: order.startDate,
        buzzId: order.buzzId
    };
};
